/**
 * The screen a hosted customer sees when they have no working plan
 * (#2454 Slice 3).
 *
 * Built to the approved mockup at `docs/mockups/2454-billing-gate.html`, on
 * the same card the onboarding screen already renders. The words live in
 * `billingGateCopy.ts`; this lays them out and wires the one action each.
 *
 * **Why sign out is always here.** The gate blocks every route except this
 * one, account settings and preferences. Without a way off the screen it is a
 * trap, and one of these states tells somebody in so many words to go and
 * change their email address.
 *
 * **Why the destination is a plan page and never a checkout.** The customer
 * may already have a plan when we get this wrong: the account service can
 * answer "no billing" when what it meant was that it could not read its own
 * table. Landing them somewhere that shows what they have makes that mistake
 * cost a confusing trip instead of a second payment. That property is half of
 * a pair, and the account service holds the other half; neither is decoration.
 */
import { useState } from 'react'
import { Button } from '@/components/ui'
import { AuthLayout } from '@/auth/AuthLayout'
import { authClient } from '@/auth/authClient'
import { GATE_COPY, type BillingGateScreen } from './billingGateCopy'

export function BillingGate({
  screen,
  handoffUrl,
}: {
  screen: BillingGateScreen
  /** Where the action goes: the account service's sign-in handoff. Null when
   * this install hands nobody over, and then no action button is shown. */
  handoffUrl?: string | null
}) {
  const copy = GATE_COPY[screen]
  const [working, setWorking] = useState(false)

  /** Where the one action goes: the account service's sign-in handoff, which
   * signs the customer in there and lands them on their own plan page showing
   * whatever they already have. None of them starts a payment directly. When
   * the handoff is not configured there is nowhere to send them, so the
   * buttons are hidden and this never runs (#2481). */
  const goToPlan = () => {
    if (!handoffUrl) return
    setWorking(true)
    window.location.assign(handoffUrl)
  }

  const signOut = async () => {
    setWorking(true)
    try {
      await authClient.signOut()
    } finally {
      // Navigate even if the call failed. A network blip must not strand
      // somebody on a screen whose whole problem is that they cannot leave.
      window.location.assign('/auth/login')
    }
  }

  return (
    <AuthLayout title={copy.title} subtitle={copy.subtitle}>
      <div className="rounded-md border border-border bg-muted/30 p-4">
        <p className="text-sm" data-testid="billing-gate-body">
          {copy.body}
        </p>

        {copy.aside && (
          <p className="text-xs text-muted-foreground mt-2" data-testid="billing-gate-aside">
            {copy.aside}
          </p>
        )}

        {handoffUrl && (copy.action || copy.secondary) && (
          <div className="flex flex-wrap gap-2 mt-3">
            {copy.action && (
              <Button
                size="sm"
                variant={copy.action.kind === 'primary' ? 'default' : 'outline'}
                onClick={goToPlan}
                disabled={working}
                data-testid="billing-gate-action"
              >
                {copy.action.label}
              </Button>
            )}
            {copy.secondary && (
              <Button
                size="sm"
                variant="outline"
                onClick={goToPlan}
                disabled={working}
                data-testid="billing-gate-secondary"
              >
                {copy.secondary.label}
              </Button>
            )}
          </div>
        )}

        {/* The way off the screen. Present on every state, because the gate
            holds every other route and one of these states sends somebody to
            settings to change their email. */}
        <div className="flex flex-wrap gap-4 mt-3 pt-3 border-t border-border text-xs">
          <a
            href="/account"
            className="text-muted-foreground underline-offset-4 hover:underline"
            data-testid="billing-gate-settings"
          >
            Account settings
          </a>
          <button
            type="button"
            onClick={() => void signOut()}
            disabled={working}
            className="text-muted-foreground underline-offset-4 hover:underline"
            data-testid="billing-gate-signout"
          >
            Sign out
          </button>
        </div>
      </div>
    </AuthLayout>
  )
}
